import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { HexagonPattern, CrosshairMark } from './GeometricPatterns';

const formatNOK = (amount) =>
  new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', minimumFractionDigits: 0 })?.format(amount);

const StatBlock = ({ icon, label, value, accent = false, delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className="relative flex-1"
      style={{
        padding: '16px 20px',
        borderRight: '1px solid var(--color-border)',
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        opacity: visible ? 1 : 0,
        transition: `transform 500ms cubic-bezier(0.34, 1.2, 0.64, 1) ${delay}ms, opacity 400ms ease-out ${delay}ms`,
      }}
    >
      <CrosshairMark className="top-1 right-1" size={12} color={accent ? '#FF5722' : '#004F98'} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '6px',
        }}
      >
        <Icon name={icon} size={14} color={accent ? '#FF5722' : 'var(--color-muted-foreground)'} />
        <span
          style={{
            fontFamily: 'var(--font-caption)',
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--color-muted-foreground)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          fontFamily: 'var(--font-data)',
          fontWeight: 700,
          fontSize: '20px',
          color: accent ? '#FF5722' : 'var(--color-foreground)',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
    </div>
  );
};

const TripSummaryStats = ({ trips = [] }) => {
  const totalCost = trips?.reduce((s, t) => s + (t?.totalCost || 0), 0);
  const totalSavings = trips?.reduce((s, t) => s + (t?.discount || 0), 0);
  const evTrips = trips?.filter((t) => t?.vehicleType === 'ev')?.length;

  return (
    <div
      className="relative overflow-hidden"
      style={{
        background: 'var(--color-card)',
        border: '2px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        flexWrap: 'wrap',
      }}
    >
      <HexagonPattern className="top-0 right-0 opacity-30" opacity={0.04} />
      <StatBlock icon="Route" label="Totale reiser" value={trips?.length} delay={0} />
      <StatBlock icon="CreditCard" label="Total kostnad" value={formatNOK(totalCost)} delay={80} />
      <StatBlock icon="TrendingDown" label="Total besparelse" value={formatNOK(totalSavings)} accent delay={160} />
      <StatBlock
        icon="Zap"
        label="EV-reiser"
        value={`${evTrips} / ${trips?.length}`}
        delay={240}
      />
    </div>
  );
};

export default TripSummaryStats;
